// Player-controlled chef ("Stardust Bubble" barista)
import { Light } from '../engine/renderer.js';
import { col, lerp, angleLerp } from '../engine/math.js';
import { buildChef } from './models.js';
import { CHEF_COLORS, CHEF_NAMES } from './data.js';

export class Chef {
  constructor(game, index, x, z) {
    this.game = game;
    this.index = index;
    this.color = CHEF_COLORS[index % 4];
    this.name = CHEF_NAMES[index % 4];
    this.parts = buildChef(this.color);
    this.node = this.parts.root;
    this.x = x;
    this.z = z;
    this.spawn = [x, z];
    this.vx = 0;
    this.vz = 0;
    this.face = Math.PI; // facing north (towards -z)
    this.held = null;
    this.target = null;
    this.dashT = 0;
    this.dashCD = 0;
    this.walkT = 0;
    this.active = true; // for solo swap: the one being controlled
    this.controller = null;
    this.useHeld = false;
    this.falling = 0;
    this.respawn = 0;
    this.tpLock = false;
    this.squash = 0;
    // online (see Game._bindChef): played here or elsewhere, and how often the host moved it itself
    this.local = true;
    this.remote = false;
    this.netLock = false;
    this.warp = 0;
    this.lamp = new Light({ position: [x, 1.4, z], color: this.color, intensity: 0.8, range: 6, dir: [0, -0.6, 0.8], cone: 0.55, soft: 0.5, priority: 2 });
    game.scene.lights.push(this.lamp);
    this.glowL = new Light({ position: [x, 1.0, z], color: this.color, intensity: 0.6, range: 1.6 });
    game.scene.lights.push(this.glowL);
    game.scene.add(this.node);
    this.node.position = [x, 0, z];
    this.node.scale = [1.12, 1.12, 1.12];
    this.node.rotation[1] = this.face;
    this.ringNode = null;
  }
  get fx() {
    return Math.sin(this.face);
  }
  get fz() {
    return Math.cos(this.face);
  }
  /** If pushing straight into a wall but a free lane is just beside us, slide towards it. */
  _cornerAssist(g, mx, mz, r, dt) {
    const ax = Math.abs(mx) > Math.abs(mz);
    const dx = ax ? Math.sign(mx) : 0, dz = ax ? 0 : Math.sign(mz);
    const probe = 0.12;
    if (!g.solidCircle(this.x + dx * probe, this.z + dz * probe, r)) return; // not blocked
    for (const off of [0.18, 0.3, 0.42]) {
      for (const sgn of [1, -1]) {
        const px = ax ? 0 : sgn * off, pz = ax ? sgn * off : 0;
        if (!g.solidCircle(this.x + px, this.z + pz, r) && !g.solidCircle(this.x + px + dx * probe, this.z + pz + dz * probe, r)) {
          const step = Math.min(off, 3.2 * dt);
          [this.x, this.z] = g.pushOut(this.x + Math.sign(px) * step, this.z + Math.sign(pz) * step, r);
          return;
        }
      }
    }
  }
  hold(item) {
    this.held = item;
    if (item) {
      item.holder = this;
      this.parts.hand.add(item.node);
      item.node.position = [0, 0, 0];
      item.node.rotation = [0, 0, 0];
      item.node.scale = [1, 1, 1];
    }
  }
  release() {
    const it = this.held;
    if (it) {
      this.parts.hand.remove(it.node);
      it.holder = null;
    }
    this.held = null;
    return it;
  }
  update(dt, t, ctrl, darkness) {
    const g = this.game;
    // falling and respawning are the host's call; a guest's screen follows along
    if (!g.mirror && this.respawn > 0) {
      this.respawn -= dt;
      this.node.visible = false;
      if (this.respawn <= 0) {
        this.node.visible = true;
        this.x = this.spawn[0];
        this.z = this.spawn[1];
        this.vx = this.vz = 0;
        this.node.position = [this.x, 3, this.z];
        this.squash = 1;
        this.warp++;
        g.fxSpawn(this.x, this.z, this.color);
      }
      this.lamp.intensity = 0;
      this.glowL.intensity = 0;
      return;
    }
    if (!g.mirror && this.falling > 0) {
      this.falling += dt;
      this.node.position[1] -= dt * (4 + this.falling * 12);
      this.node.rotation[0] += dt * 3;
      if (this.falling > 1.2) {
        this.falling = 0;
        this.node.rotation[0] = 0;
        this.respawn = 3;
      }
      return;
    }
    if (this.remote || this.netLock) return this._follow(dt, t, darkness);
    if (!this.node.visible) {
      // an online guest's own chef, back from a respawn the host ran
      this.node.visible = true;
      this.node.position = [this.x, 3, this.z];
      this.node.rotation[0] = 0;
      this.squash = 1;
    }
    const speedMul = g.perkActive('speed') ? 1.4 : 1;
    const maxSpeed = 4.0 * speedMul;
    let mx = 0, mz = 0;
    if (ctrl && this.active) {
      mx = ctrl.mx;
      mz = ctrl.mz;
    }
    const k = 1 - Math.exp(-30 * dt);
    this.vx = lerp(this.vx, mx * maxSpeed, k);
    this.vz = lerp(this.vz, mz * maxSpeed, k);
    if (this.dashCD > 0) this.dashCD -= dt;
    if (ctrl && this.active && ctrl.dash && this.dashCD <= 0) {
      this.dashT = 0.16;
      this.dashCD = 0.5;
      g._dashFx(this);
    }
    let vx = this.vx, vz = this.vz;
    if (this.dashT > 0) {
      this.dashT -= dt;
      vx += this.fx * 9;
      vz += this.fz * 9;
    }
    // move with collision
    const r = 0.3;
    // move in small steps and push out of walls: corners deflect instead of snagging
    const dist = Math.hypot(vx, vz) * dt;
    const n = Math.max(1, Math.ceil(dist / 0.12));
    for (let i = 0; i < n; i++) {
      [this.x, this.z] = g.pushOut(this.x + (vx * dt) / n, this.z + (vz * dt) / n, r);
    }
    // corner assist: brushing past the very edge of a counter nudges you into the gap
    if (Math.hypot(mx, mz) > 0.5) this._cornerAssist(g, mx, mz, r, dt);
    const moving = Math.hypot(mx, mz) > 0.1;
    if (moving) this.face = angleLerp(this.face, Math.atan2(mx, mz), 1 - Math.exp(-24 * dt));
    this._pose(dt, t, darkness, vx, vz, moving, this.x, this.z);
  }
  /** A chef moved from elsewhere: another player's screen (online host), or the host (online guest). */
  _follow(dt, t, darkness) {
    if (this.respawn > 0) {
      this.node.visible = false;
      this.lamp.intensity = 0;
      this.glowL.intensity = 0;
      return;
    }
    if (!this.node.visible || this.landing) {
      // back from a respawn: drop in from above, like on the host
      this.node.visible = true;
      this.landing = false;
      this.node.position = [this.x, 3, this.z];
      this.node.rotation[0] = 0;
      this.squash = 1;
      this.dispX = this.x;
      this.dispZ = this.z;
    }
    if (this.falling > 0) {
      this.node.position[1] -= dt * (4 + this.falling * 12);
      this.node.rotation[0] += dt * 3;
      return;
    }
    // glide to the latest reported spot, so 20 to 30 updates a second still look smooth
    if (this.dispX == null || Math.hypot(this.x - this.dispX, this.z - this.dispZ) > 1.5) {
      this.dispX = this.x;
      this.dispZ = this.z;
    }
    const k = 1 - Math.exp(-18 * dt);
    this.dispX += (this.x - this.dispX) * k;
    this.dispZ += (this.z - this.dispZ) * k;
    if (this.dashT > 0) this.dashT = Math.max(0, this.dashT - dt);
    const vx = this.vx + (this.dashT > 0 ? this.fx * 9 : 0), vz = this.vz + (this.dashT > 0 ? this.fz * 9 : 0);
    this._pose(dt, t, darkness, vx, vz, Math.hypot(this.vx, this.vz) > 0.4, this.dispX, this.dispZ);
  }
  /** Walk cycle, arms, helmet lamp: how the chef looks this frame. */
  _pose(dt, t, darkness, vx, vz, moving, px, pz) {
    const p = this.parts;
    const sp = Math.hypot(vx, vz);
    this.walkT += dt * (4 + sp * 3.2);
    const bob = moving ? Math.abs(Math.sin(this.walkT)) * 0.06 * Math.min(1, sp / 2) : 0;
    this.squash = Math.max(0, this.squash - dt * 4);
    const sq = Math.sin(this.squash * Math.PI) * 0.18;
    this.node.position = [px, lerp(this.node.position[1], 0, 1 - Math.exp(-12 * dt)), pz];
    this.node.rotation[1] = this.face;
    p.body.position[1] = bob;
    p.body.scale = [1 + sq, 1 - sq, 1 + sq];
    // lean into the run (more while dashing), on a spring so it eases in and settles back rather than snapping
    const leanTo = Math.min(0.25, Math.hypot(this.vx, this.vz) * 0.04) + (this.dashT > 0 ? 0.28 : 0);
    const h = Math.min(dt, 1 / 30);
    this.leanV = (this.leanV || 0) + ((leanTo - (this.lean || 0)) * 220 - (this.leanV || 0) * 24) * h;
    this.lean = (this.lean || 0) + this.leanV * h;
    p.body.rotation[0] = this.lean;
    const swing = Math.sin(this.walkT) * 0.6 * Math.min(1, sp / 2);
    if (this.held) {
      p.armL.rotation = [-1.2, 0, 0.2];
      p.armR.rotation = [-1.2, 0, -0.2];
    } else if (this.useHeld && this.job && this.job.kind === 'chop' && this.job.knife) {
      // knife hand rises and falls with the knife; the other hand steadies the food
      p.armR.rotation = [-0.7 - this.job.knifeLift * 0.75, 0, -0.25];
      p.armL.rotation = [-1.15, 0, 0.3];
    } else if (this.useHeld) {
      const c = Math.sin(t * 22) * 0.5;
      p.armL.rotation = [-1.0 + c, 0, 0.2];
      p.armR.rotation = [-1.0 - c, 0, -0.2];
    } else {
      p.armL.rotation = [swing, 0, 0.15];
      p.armR.rotation = [-swing, 0, -0.15];
    }
    p.head.rotation[2] = Math.sin(this.walkT * 0.5) * 0.05;
    p.bulb.scale = [0.05, 0.05, 0.05].map((v) => v * (1 + Math.sin(t * 5 + this.index) * 0.15));
    // helmet lamp follows facing
    const lampBoost = darkness;
    this.lamp.position = [px + this.fx * 0.2, 1.35, pz + this.fz * 0.2];
    const d = [this.fx * 0.75, -0.65, this.fz * 0.75];
    const l = Math.hypot(d[0], d[1], d[2]);
    this.lamp.dir = [d[0] / l, d[1] / l, d[2] / l];
    this.lamp.intensity = 1.5 + lampBoost * 24;
    this.lamp.range = 6 + lampBoost * 2;
    this.lamp.cone = 0.55 + lampBoost * 0.15;
    this.glowL.position = [px, 1.1, pz];
    this.glowL.intensity = (0.8 + lampBoost * 2.5) * (this.active ? 1 : 0.5);
    // inactive chef (solo): dim
    p.helmet.tint[3] = this.active ? 0.9 : 0.5;
  }
}
